import { useState } from 'react';
import { ExternalLink, Link2, Loader2, Receipt, Unlink } from 'lucide-react';
import { twMerge } from 'tailwind-merge';

import { useLeadInvoices, type LeadInvoice } from '../../../hooks/useLeadInvoices';
import { sensitive } from '../../../lib/presentationMode';
import { INVOICE_CATEGORY_LABELS, invoiceCategoryOf } from '../../../utils/revenue';
import { BLOCK_ACTION, BLOCK_ROW, CHIP, FIELD, TONE, TOOL_SM } from '../../bureau/encre';
import { Pastille, SheetBlock, SheetEmpty, SheetLoading } from './EncreSheet';

const euros = (cents: number) => sensitive(`${(cents / 100).toLocaleString('fr-FR', { maximumFractionDigits: 2 })} €`);
const day = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' }) : 'sans date');

function InvoiceRow({
  invoice,
  tied,
  busy,
  onTie,
  onUntie,
  onCategory,
}: {
  invoice: LeadInvoice;
  tied: boolean;
  busy: boolean;
  onTie: () => void;
  onUntie: () => void;
  onCategory: (category: string) => void;
}) {
  const paid = invoice.status === 'paid';
  const which = `${euros(invoice.amount_paid || invoice.amount_due)} du ${day(invoice.invoice_date)}`;
  return (
    <li className="flex min-w-0 items-center gap-3 py-2">
      <Pastille icon={Receipt} />
      <div className="min-w-0 flex-1">
        <p className="flex flex-wrap items-center gap-x-2 text-sm text-desk-ink">
          <span className="font-medium">{euros(invoice.amount_paid || invoice.amount_due)}</span>
          <span className={twMerge(CHIP, paid ? TONE.success : TONE.warning)}>{paid ? 'Payée' : 'En attente'}</span>
          {!tied && <span className={twMerge(CHIP, TONE.neutral)}>pas rattachée</span>}
        </p>
        <p className="truncate text-xs text-desk-soft">
          {day(invoice.invoice_date)}
          {invoice.description ? ` · ${invoice.description}` : ''}
        </p>
      </div>
      {tied && (
        <label className="shrink-0">
          <span className="sr-only">Catégorie de revenu de la facture de {which}</span>
          <select
            value={invoiceCategoryOf(invoice)}
            disabled={busy}
            onChange={(e) => onCategory(e.target.value)}
            className={twMerge(FIELD, 'h-8 w-auto px-2 text-xs')}
          >
            {Object.entries(INVOICE_CATEGORY_LABELS).map(([key, label]) => (
              <option key={key} value={key}>
                {label}
              </option>
            ))}
          </select>
        </label>
      )}
      {busy && <Loader2 size={15} className="animate-spin text-desk-muted" aria-label="Enregistrement…" />}
      {invoice.hosted_invoice_url && (
        <a href={invoice.hosted_invoice_url} target="_blank" rel="noopener noreferrer" className={TOOL_SM} aria-label={`Ouvrir la facture de ${which}`}>
          <ExternalLink size={14} aria-hidden="true" />
        </a>
      )}
      {tied ? (
        <button type="button" onClick={onUntie} disabled={busy} className={TOOL_SM} aria-label={`Détacher la facture de ${which}`}>
          <Unlink size={14} aria-hidden="true" />
        </button>
      ) : (
        <button type="button" onClick={onTie} disabled={busy} className={TOOL_SM} aria-label={`Rattacher la facture de ${which}`}>
          <Link2 size={14} aria-hidden="true" />
        </button>
      )}
    </li>
  );
}

/**
 * « Factures »: the lead's manual invoices — those tied to it, and its Stripe customer's not tied
 * yet — their category (it decides whether the invoice MRR counts them), and tying another one
 * picked among the unlinked manual invoices. The only place an invoice is tied to a lead.
 */
export function LeadInvoicesBlock({ leadId, customerId }: { leadId: string; customerId: string | null }) {
  const { invoices, ready, readError, writeError, busy, link, unlink, setCategory, listUnlinked } = useLeadInvoices(leadId, customerId);
  const [picking, setPicking] = useState<{ invoices: LeadInvoice[]; error: string | null } | null>(null);
  const tiedCount = invoices.filter((i) => i.lead_id === leadId).length;

  return (
    <SheetBlock
      title="Factures"
      count={tiedCount}
      action={
        !picking && (
          <button type="button" onClick={() => void listUnlinked().then(setPicking)} className={BLOCK_ACTION}>
            Rattacher une facture
          </button>
        )
      }
    >
      {writeError && <p role="alert" className="mb-2 text-sm text-red-600 dark:text-red-400">Pas enregistré : {writeError}</p>}
      {readError ? (
        <p className="text-sm text-red-600 dark:text-red-400">Lecture impossible : {readError}</p>
      ) : !ready ? (
        <SheetLoading />
      ) : invoices.length === 0 ? (
        <SheetEmpty>Aucune facture manuelle rattachée.</SheetEmpty>
      ) : (
        <ul className="m-0 list-none divide-y divide-desk-line p-0">
          {invoices.map((invoice) => (
            <InvoiceRow
              key={invoice.id}
              invoice={invoice}
              tied={invoice.lead_id === leadId}
              busy={busy === invoice.id}
              onTie={() => void link(invoice.id)}
              onUntie={() => void unlink(invoice.id)}
              onCategory={(category) => void setCategory(invoice.id, category)}
            />
          ))}
        </ul>
      )}
      {picking && (
        <div className="mt-3 rounded-2xl bg-desk-card p-3 shadow-lift">
          <div className="mb-2 flex items-center justify-between">
            <p className="text-xs font-medium text-desk-soft">Factures manuelles rattachées à personne</p>
            <button type="button" autoFocus onClick={() => setPicking(null)} className={BLOCK_ACTION}>
              Fermer
            </button>
          </div>
          {picking.error ? (
            <p className="text-sm text-red-600 dark:text-red-400">Lecture impossible : {picking.error}</p>
          ) : picking.invoices.length === 0 ? (
            <SheetEmpty>Aucune facture à rattacher.</SheetEmpty>
          ) : (
            <ul className="m-0 max-h-64 list-none overflow-y-auto p-0">
              {picking.invoices.map((invoice) => (
                <li key={invoice.id}>
                  <button
                    type="button"
                    onClick={() => {
                      setPicking(null);
                      void link(invoice.id);
                    }}
                    className={BLOCK_ROW}
                  >
                    <span className="min-w-0 flex-1 truncate text-sm text-desk-ink">
                      {day(invoice.invoice_date)} — {euros(invoice.amount_paid || invoice.amount_due)}
                      {invoice.description ? <span className="text-desk-soft"> · {invoice.description}</span> : null}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </SheetBlock>
  );
}
